import { useState, useMemo, useRef, useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { twMerge } from 'tailwind-merge';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { AgentAvatar } from '@/entities/agent';
import type { AgentThinkingProcessProps } from './AgentThinkingProcess.types';
import {
  FADE_OVERLAY_CLASSES,
  SHIMMER_BORDER_CLASSES,
  SHIMMER_CLASSES,
} from './AgentThinkingProcess.styles';
import { containerVariants } from './AgentThinkingProcess.constants';
import { useTranslation } from 'react-i18next';

const SWEEP = { backgroundPosition: ['-200% 0', '200% 0'] };
const SWEEP_TRANSITION = { duration: 2, ease: 'easeInOut', repeat: Infinity } as const;

export const AgentThinkingProcess = ({
  thinkingProcesses,
  isThinking,
  hidden = false,
}: AgentThinkingProcessProps) => {
  const { t } = useTranslation('chats');

  const [isOpen, setIsOpen] = useState(false);
  const collapsedTextRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  // Concatenate all thinking process texts when collapsed
  const thinkingText = useMemo(() => {
    if (isOpen || !thinkingProcesses || thinkingProcesses.filter(Boolean).length === 0) {
      return null;
    }
    return thinkingProcesses.map((p) => p.text).join('\n');
  }, [thinkingProcesses, isOpen]);
  const hasThoughts = !!thinkingProcesses?.filter(({ text }) => !!text).length;

  // Auto-scroll to bottom when text updates in collapsed mode
  useEffect(() => {
    if (!isOpen && collapsedTextRef.current) {
      collapsedTextRef.current.scrollTop = collapsedTextRef.current.scrollHeight;
    }
  }, [thinkingText, isOpen]);

  if ((!isThinking && !hasThoughts) || hidden) return null;

  // A sweep that never stops is the one thing Reduce Motion must silence outright,
  // so the props are dropped rather than zeroed.
  const sweep = isThinking && !prefersReducedMotion ? SWEEP : undefined;
  const sweepTransition = isThinking && !prefersReducedMotion ? SWEEP_TRANSITION : undefined;

  const ChevronIcon = isOpen ? ChevronUpIcon : ChevronDownIcon;

  const toggle = (
    <motion.button
      type="button"
      aria-expanded={isOpen}
      animate={sweep}
      transition={sweepTransition}
      onClick={() => setIsOpen(!isOpen)}
      className={twMerge(
        SHIMMER_CLASSES,
        SHIMMER_BORDER_CLASSES,
        'mb-2 flex w-fit cursor-pointer items-center justify-between gap-2',
      )}
    >
      <span className="text-xs leading-5 text-muted-foreground">
        {hasThoughts
          ? t('chats.agentThinkingProcess.reasoning')
          : t('chats.agentThinkingProcess.thinking')}
      </span>
      {hasThoughts && <ChevronIcon aria-hidden className="size-4 text-muted-foreground" />}
    </motion.button>
  );

  return (
    <div className="mt-1 mb-1">
      {isOpen ? (
        <div>
          {toggle}

          {hasThoughts && (
            <AnimatePresence mode="popLayout">
              {thinkingProcesses
                .filter(({ text }) => !!text)
                .map(({ id, text, agentId }) => (
                  <motion.div
                    key={id}
                    variants={containerVariants}
                    initial={prefersReducedMotion ? false : 'initial'}
                    animate="animate"
                    exit="exit"
                    transition={
                      prefersReducedMotion ? undefined : { duration: 0.2, ease: 'easeOut' }
                    }
                  >
                    <motion.div
                      animate={sweep}
                      transition={sweepTransition}
                      className={twMerge(SHIMMER_CLASSES, 'flex items-start gap-2')}
                    >
                      <p className="min-w-0 flex-1 whitespace-pre-line text-xs italic leading-5 text-muted-foreground">
                        {text}
                      </p>
                      <div className="shrink-0">
                        <AgentAvatar agentId={agentId} variant="small" />
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
            </AnimatePresence>
          )}
        </div>
      ) : (
        <div className="relative">
          {toggle}
          {thinkingText && (
            <div className="relative overflow-hidden">
              <motion.div
                ref={collapsedTextRef}
                animate={sweep}
                transition={sweepTransition}
                /* Three lines tall, scrollable but with the scrollbar and any
                   scroll chaining suppressed so the thread behind keeps the gesture. */
                className={twMerge(
                  SHIMMER_CLASSES,
                  'max-h-[calc(1.4rem*3)] touch-none overflow-x-hidden overflow-y-auto overscroll-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                )}
              >
                <p className="whitespace-pre-line text-xs italic leading-[1.4] text-muted-foreground">
                  {thinkingText}
                </p>
                <div className={FADE_OVERLAY_CLASSES} />
              </motion.div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
